import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { cancelPayment, getPayment, type PaymentOrder } from '../services/payment.api';

const POLL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60_000;
const FINAL = new Set(['SUCCESS', 'FAILED', 'EXPIRED', 'CANCELED', 'NEEDS_REVIEW', 'REFUNDED']);

const formatVnd = (amount: number, locale: string) =>
  new Intl.NumberFormat(locale === 'vi' ? 'vi-VN' : 'en-US', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(amount);

/** Landing page after the SePay hosted checkout: the order status is polled from our API. */
export const PaymentResultPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const txId = params.get('tx');
  const gatewayStatus = params.get('status'); // error | cancel | null
  const [order, setOrder] = useState<PaymentOrder | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const startedAt = useRef(Date.now());
  const { refreshSubscription, refreshQuota } = useAuth();
  const succeeded = order?.status === 'SUCCESS';

  // The grant can land a moment after the status flips, so refresh the badge/quota twice.
  useEffect(() => {
    if (!succeeded) return;
    void refreshSubscription();
    void refreshQuota();
    const timer = setTimeout(() => {
      void refreshSubscription();
      void refreshQuota();
    }, 3000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [succeeded]);

  const load = useCallback(async () => {
    if (!txId) return null;
    const res = await getPayment(txId);
    if (res.success && res.data) {
      setOrder(res.data);
      setLoadError(false);
      return res.data;
    }
    setLoadError(true);
    return null;
  }, [txId]);

  useEffect(() => {
    if (!txId) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      const next = await load();
      if (stopped) return;
      if (next && FINAL.has(next.status)) return;
      if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
        setTimedOut(true);
        return;
      }
      timer = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [txId, load]);

  const handleCancel = async () => {
    if (!txId) return;
    setCanceling(true);
    const res = await cancelPayment(txId);
    if (res.success && res.data) setOrder(res.data);
    setCanceling(false);
  };

  const status = order?.status;
  const pending = !status || status === 'PENDING';

  return (
    <div className="mx-auto max-w-lg p-6">
      <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100">
          {t('subscription.paymentResult.title', 'Payment')}
        </h1>

        {!txId && (
          <Alert severity="warning">
            {t('subscription.paymentResult.missing', 'Order not found.')}
          </Alert>
        )}

        {txId && pending && !timedOut && (
          <div className="flex items-center gap-3 text-gray-700 dark:text-slate-300">
            <CircularProgress size={22} />
            <span>
              {gatewayStatus === 'cancel'
                ? t(
                    'subscription.paymentResult.canceledAtGateway',
                    'Payment was not completed. You can retry or cancel the order.'
                  )
                : t('subscription.paymentResult.waiting', 'Waiting for payment confirmation…')}
            </span>
          </div>
        )}
        {txId && pending && timedOut && (
          <Alert severity="info">
            {t(
              'subscription.paymentResult.stillPending',
              'We have not received the payment yet. If you already paid, it will be applied automatically within a few minutes.'
            )}
          </Alert>
        )}
        {status === 'SUCCESS' && (
          <Alert severity="success">
            {t(
              'subscription.paymentResult.success',
              'Payment successful. Your purchase has been applied.'
            )}
          </Alert>
        )}
        {status === 'NEEDS_REVIEW' && (
          <Alert severity="warning">
            {t(
              'subscription.paymentResult.needsReview',
              'We received your payment but need to verify it. Our team will resolve this shortly.'
            )}
          </Alert>
        )}
        {(status === 'FAILED' || status === 'EXPIRED' || status === 'CANCELED') && (
          <Alert severity="error">
            {t(`subscription.paymentResult.${status}`, 'This payment was not completed.')}
          </Alert>
        )}
        {loadError && !order && (
          <Alert severity="error">
            {t(
              'subscription.paymentResult.loadFailed',
              'Could not load the order. Please try again.'
            )}
          </Alert>
        )}

        {order && (
          <div className="space-y-1 text-sm text-gray-700 dark:text-slate-300">
            {order.item && <div className="font-medium">{order.item.name}</div>}
            {order.discountMinor > 0 && (
              <div className="flex justify-between">
                <span>{t('subscription.payment.discount', 'Discount')}</span>
                <span>-{formatVnd(order.discountMinor, i18n.language)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold">
              <span>{t('subscription.payment.total', 'Total to pay')}</span>
              <span>{formatVnd(order.amountMinor, i18n.language)}</span>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          <Button variant="contained" onClick={() => navigate('/subscription')}>
            {t('subscription.paymentResult.back', 'Back to subscription')}
          </Button>
          {txId && status === 'PENDING' && (
            <Button color="inherit" disabled={canceling} onClick={() => void handleCancel()}>
              {t('subscription.paymentResult.cancelOrder', 'Cancel order')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentResultPage;
